import { useEffect, useState } from 'react'

import { api, can, refusalReason, type RiskMatrixConfig, type RiskMatrixInput } from '../lib/api'
import { Status, Toggletip } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import '../styles/sweep.css'

// The tenant's likelihood x impact matrix: size, axis labels, thresholds, the
// appetite line, and the two-person lock. Field for field the screen FOCTTA
// Core has under Settings, so a customer who runs both sets the numbers
// once in each and sees the same cell light up in both.

const DEFAULT_LABELS: Record<number, { l: string[]; i: string[] }> = {
  3: { l: ['Low', 'Medium', 'High'], i: ['Minor', 'Moderate', 'Major'] },
  4: { l: ['Low', 'Medium', 'High', 'Very high'], i: ['Minor', 'Moderate', 'Major', 'Severe'] },
  5: { l: ['Very low', 'Low', 'Medium', 'High', 'Very high'], i: ['Negligible', 'Minor', 'Moderate', 'Major', 'Catastrophic'] },
}
const DEFAULT_THRESHOLDS: Record<number, { m: number; h: number; c: number; a: number }> = {
  3: { m: 3, h: 4, c: 6, a: 4 },
  4: { m: 4, h: 6, c: 9, a: 6 },
  5: { m: 5, h: 10, c: 16, a: 10 },
}

function severityOf(score: number, f: RiskMatrixInput): string {
  if (score >= f.criticalAt) return 'critical'
  if (score >= f.highAt) return 'high'
  if (score >= f.mediumAt) return 'medium'
  return 'low'
}

export function RiskMatrixSettings() {
  const [cfg, setCfg] = useState<RiskMatrixConfig | null>(null)
  const [form, setForm] = useState<RiskMatrixInput | null>(null)
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [signOff, setSignOff] = useState('')
  const [reason, setReason] = useState('')

  function load() {
    api
      .riskMatrix()
      .then((r) => {
        setCfg(r.riskMatrix)
        setForm({
          matrixSize: r.riskMatrix.matrixSize,
          likelihoodLabels: r.riskMatrix.likelihoodLabels,
          impactLabels: r.riskMatrix.impactLabels,
          mediumAt: r.riskMatrix.mediumAt,
          highAt: r.riskMatrix.highAt,
          criticalAt: r.riskMatrix.criticalAt,
          appetiteAt: r.riskMatrix.appetiteAt,
          inherentResidual: r.riskMatrix.inherentResidual,
        })
        setNote(r.note)
      })
      .catch((e) => setError((e as Error).message ?? String(e)))
  }
  useEffect(load, [])

  if (error && !form) return <p className="note warn">{error}</p>
  if (!cfg || !form) return null

  const locked = cfg.locked
  const max = form.matrixSize * form.matrixSize

  function resize(n: number) {
    if (!form) return
    setForm({ ...form, matrixSize: n, likelihoodLabels: DEFAULT_LABELS[n]!.l, impactLabels: DEFAULT_LABELS[n]!.i, mediumAt: DEFAULT_THRESHOLDS[n]!.m, highAt: DEFAULT_THRESHOLDS[n]!.h, criticalAt: DEFAULT_THRESHOLDS[n]!.c, appetiteAt: DEFAULT_THRESHOLDS[n]!.a })
  }

  async function run(what: () => Promise<{ riskMatrix: RiskMatrixConfig; note: string }>) {
    setBusy(true)
    setError('')
    try {
      const r = await what()
      setCfg(r.riskMatrix)
      setNote(r.note)
      setSignOff('')
      setReason('')
      load()
    } catch (e) {
      setError((e as Error).message ?? String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="p1-page rm-settings">
      <div className="p1-head">
        <div>
          <p className="muted">
            <a href="#/settings">Settings</a>
          </p>
          <h1>Risk matrix</h1>
          {/* Where the matrix stands, as a label; the API's sentence on what
              happens next is kept word for word behind the (i). */}
          <p className="sw-row">
            {cfg.locked ? (
              <Status tone="plain">Signed off</Status>
            ) : !cfg.configured ? (
              <Status tone="neutral">Shipped defaults</Status>
            ) : (
              <Status tone="warn">Awaiting sign-off</Status>
            )}
            <ReasonTip label="Where this matrix stands" text={note} />
          </p>
        </div>
        <div className="p1-actions">
          <a className="button" href="#/risk">
            See the matrix
          </a>
        </div>
      </div>

      {error && <p className="note warn">{error}</p>}

      <section className="panel">
        <h2>Size and axis labels</h2>
        <div className="rm-sizes">
          {[3, 4, 5].map((n) => (
            <button type="button" key={n} className={'chip' + (form.matrixSize === n ? ' strong' : '')} disabled={locked} onClick={() => resize(n)}>
              {n}×{n}
            </button>
          ))}
        </div>
        <div className="rm-labels">
          <div>
            <h3>Likelihood, low to high</h3>
            {form.likelihoodLabels.map((l, i) => (
              <label key={i}>
                <span className="mono">L{i + 1}</span>
                <input value={l} disabled={locked} onChange={(e) => setForm({ ...form, likelihoodLabels: form.likelihoodLabels.map((x, j) => (j === i ? e.target.value : x)) })} />
              </label>
            ))}
          </div>
          <div>
            <h3>Impact, low to high</h3>
            {form.impactLabels.map((l, i) => (
              <label key={i}>
                <span className="mono">I{i + 1}</span>
                <input value={l} disabled={locked} onChange={(e) => setForm({ ...form, impactLabels: form.impactLabels.map((x, j) => (j === i ? e.target.value : x)) })} />
              </label>
            ))}
          </div>
        </div>
      </section>

      <section className="panel">
        <h2>Severity thresholds and risk appetite</h2>
        <p className="muted">
          Score = likelihood × impact, from 1 to {max}{' '}
          <Toggletip label="How a threshold reads">Each threshold is the lowest score in that bucket.</Toggletip>
        </p>
        <div className="rm-thresholds">
          {(
            [
              ['mediumAt', 'Medium from'],
              ['highAt', 'High from'],
              ['criticalAt', 'Critical from'],
              ['appetiteAt', 'Appetite line'],
            ] as const
          ).map(([k, label]) => (
            <label key={k}>
              <span>{label}</span>
              <input type="number" min={1} max={max} value={form[k]} disabled={locked} onChange={(e) => setForm({ ...form, [k]: Number(e.target.value) })} />
            </label>
          ))}
        </div>
        {/* The (i) sits beside the label rather than in it, so the guidance is
            not read as part of the checkbox's name. */}
        <div className="sw-row">
          <label className="rm-switch">
            <input type="checkbox" checked={form.inherentResidual} disabled={locked} onChange={(e) => setForm({ ...form, inherentResidual: e.target.checked })} />
            <span>
              <strong>Inherent and residual</strong> Record the risk before and after mitigation
            </span>
          </label>
          <Toggletip label="Why both are recorded">RBI operational risk guidance asks regulated entities for both.</Toggletip>
        </div>
      </section>

      <section className="panel">
        <h2>Preview</h2>
        <div className="risk-matrix preview" style={{ gridTemplateColumns: 'auto repeat(' + form.matrixSize + ', minmax(0, 1fr))' }}>
          <div className="rm-corner">
            <span className="rm-axis-y">Likelihood</span>
          </div>
          {form.impactLabels.map((label, i) => (
            <div className="rm-col-head" key={i}>
              <b>{i + 1}</b> {label}
            </div>
          ))}
          {Array.from({ length: form.matrixSize }, (_, k) => form.matrixSize - k).map((l) => (
            <PreviewRow key={l} l={l} form={form} />
          ))}
          <div />
          <div className="rm-axis-x" style={{ gridColumn: '2 / -1' }}>
            Impact
          </div>
        </div>
      </section>

      <div className="rm-actions">
        {!locked && (
          <button type="button" className="button primary" disabled={busy || !can('programme.write')} title={can('programme.write') ? undefined : refusalReason('programme.write')} onClick={() => void run(() => api.putRiskMatrix(form))} data-action="tprm.risk-matrix.a01">
            Save changes
          </button>
        )}
        {!locked && (
          <button type="button" className="button" disabled={busy || locked} onClick={() => resize(form.matrixSize)}>
            Reset to defaults
          </button>
        )}
      </div>

      <section className="panel rm-lock">
        {locked ? (
          <>
            <h2>Signed off</h2>
            <p className="sw-row">
              <span>
                Locked by <strong>{cfg.lockedBy}</strong> ({cfg.lockedRole}) on {cfg.lockedAt?.slice(0, 10)}
              </span>
              {cfg.signatureValid ? (
                <Status tone="ok">Signature matches</Status>
              ) : (
                <>
                  <Status tone="crit">Signature broken</Status>
                  <Toggletip label="What a broken signature means">
                    The signature does not match the content: something changed under the lock.
                  </Toggletip>
                </>
              )}
            </p>
            {cfg.lockNote && <p className="muted">"{cfg.lockNote}"</p>}
            {can('risk.approve') && (
              <div className="rm-lock-form">
                <input placeholder="Why it is being reopened (at least ten characters)" value={reason} onChange={(e) => setReason(e.target.value)} />
                <button type="button" className="button" disabled={busy || reason.trim().length < 10} onClick={() => void run(() => api.unlockRiskMatrix(reason))} data-action="tprm.risk-matrix.a02">
                  Unlock with this reason
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <h2>Sign-off</h2>
            <p className="muted">
              Whoever saved it last cannot be the one who signs
              {cfg.updatedBy ? ', last saved by ' + cfg.updatedBy : ''}
            </p>
            <div className="rm-lock-form">
              <input placeholder="What was reviewed and why these thresholds are right" value={signOff} onChange={(e) => setSignOff(e.target.value)} />
              <button
                type="button"
                className="button primary"
                disabled={busy || signOff.trim().length < 10 || !can('risk.approve')}
                title={can('risk.approve') ? undefined : refusalReason('risk.approve')}
                onClick={() => void run(() => api.lockRiskMatrix(signOff))}
                data-action="tprm.risk-matrix.a03"
              >
                Lock with sign-off
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  )
}

function PreviewRow({ l, form }: { l: number; form: RiskMatrixInput }) {
  return (
    <>
      <div className="rm-row-head">
        <b>{l}</b> {form.likelihoodLabels[l - 1]}
      </div>
      {Array.from({ length: form.matrixSize }, (_, k) => k + 1).map((i) => {
        const score = l * i
        const sev = severityOf(score, form)
        return (
          <div key={i} className={'rm-cell sev-' + sev + (score >= form.appetiteAt ? ' appetite' : '')}>
            <span className="rm-count">{score}</span>
            <span className="rm-score">{sev.charAt(0).toUpperCase() + sev.slice(1)}</span>
          </div>
        )
      })}
    </>
  )
}
